import { useEffect, useState } from 'react';
import type { DeepPartial } from 'ts-essentials';

import CheckboxSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/CheckboxSetting';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import NumberSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/NumberSetting';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';
import type { Colors, NetworkTheming } from 'hb-react/types/network';

type BordersProps = ComponentSettingsTab<ButtonItemAttributes> & {
  color: {
    brandColors: Array<{ label: string; value: string }>;
    opacity?: boolean;
  };
  colorOnly?: boolean;
  displayTooltip?: boolean;
  withoutColor?: boolean;
  networkColors?: Colors;
  networkTheming?: DeepPartial<NetworkTheming>;
};

const Borders = ({
  color,
  colorOnly = false,
  defaultAttributes,
  displayTooltip = false,
  item,
  onChange,
  networkColors,
  withoutColor = false,
}: BordersProps) => {
  const [hasBorders, setHasBorders] = useState(
    item.attributes?.hasBorder || defaultAttributes?.hasBorder,
  );

  useEffect(() => {
    const newHasBorders =
      item.attributes?.hasBorder || defaultAttributes?.hasBorder;

    setHasBorders(newHasBorders);
  }, [item.attributes?.hasBorder, defaultAttributes?.hasBorder]);

  if (!item) {
    return null;
  }

  const settingProps = {
    defaultAttributes,
    item,
    onChange,
  };

  const onChangeBordersInterceptor = (props) => {
    setHasBorders(props.value);
    onChange({
      id: props.id,
      values: [
        props,
        {
          id: props.id,
          settingKey: 'attributes.borderColor',
          value: item.attributes?.borderColor || '#000000',
        },
        {
          id: props.id,
          settingKey: 'attributes.borderWidth',
          value: item.attributes?.borderWidth || 3,
        },
      ],
    });
  };

  if (colorOnly) {
    if (hasBorders) {
      return (
        <ColorSetting
          color={color}
          networkColors={networkColors}
          settingKey="borderColor"
          title={s__('Builder|Border color')}
          {...settingProps}
        />
      );
    }

    return null;
  }

  return (
    <>
      <CheckboxSetting
        item={item}
        onChange={onChangeBordersInterceptor}
        settingKey="hasBorder"
        title={s__('Builder|Borders')}
        // @ts-expect-error CheckboxSetting is not typed
        tooltip={
          displayTooltip
            ? s__(
                'Builder|If you want a border only on one button: Transparent border can be applied to maintain consistent buttons height',
              )
            : null
        }
      />
      {hasBorders && (
        <>
          {!withoutColor && (
            <ColorSetting
              color={color}
              networkColors={networkColors}
              settingKey="borderColor"
              title={s__('Builder|Borders color')}
              {...settingProps}
            />
          )}
          <NumberSetting
            defaultValue={3}
            settingKey="borderWidth"
            title={s__('Builder|Borders thickness')}
            {...settingProps}
          />
        </>
      )}
    </>
  );
};

export default Borders;
